<template>
  <hb-basic-page
    :title="$t('work_methods.title')"
    fluid
    :loading="loading"
    :extension-height="60"
    :pa-0="$vuetify.breakpoint.xsOnly"
    v-if="$isTenantTechAdmin"
  >
    <template v-if="$vuetify.breakpoint.smAndUp" v-slot:app-bar-extension>
      <v-btn @click="addMethod" color="primary" class="mb-3 ml-n1">
        <v-icon class="no-gradient" left>mdi-plus</v-icon>
        <span>{{ $t('work_methods.create_new_method') }}</span>
      </v-btn>
    </template>

    <v-btn v-if="$vuetify.breakpoint.xsOnly" @click="addMethod" color="primary" fab fixed bottom right>
      <v-icon class="no-gradient">mdi-plus</v-icon>
    </v-btn>

    <v-col cols="12" class="mb-12">
      <v-expansion-panels multiple>
        <v-expansion-panel v-for="method in methods" :key="method.id">
          <v-expansion-panel-header>
            <div class="d-flex align-center">
              <span class="subtitle-1">{{ method.name }}</span>
              <v-chip v-if="method.active === false" small class="ml-3" color="grey lighten-2">
                {{ $t('work_methods.inactive') }}
              </v-chip>
              <v-chip v-if="method.accruesDrilledMeters" small class="ml-3" color="primary" outlined>
                {{ $t('work_methods.accrues_drilled_meters') }}
              </v-chip>
              <v-chip small class="ml-3" outlined>
                {{ $t('work_methods.pricing_chip', { label: pricingLabel(method) }) }}
              </v-chip>
              <v-btn icon small class="ml-3" @click.stop="editMethod(method)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-for="group in attributeGroups" :key="group.key" class="mb-4">
              <div class="subtitle-2 mb-1">{{ $t(group.titleKey) }}</div>
              <div v-if="group.hintKey" class="text--secondary caption mb-2">{{ $t(group.hintKey) }}</div>
              <div v-if="method[group.field].length === 0" class="text--secondary mb-2">
                {{ $t('work_methods.no_attributes') }}
              </div>
              <v-simple-table v-else dense class="aligned-attr-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th style="width: 48px"></th>
                      <th style="width: 22%">{{ $t('work_methods.label') }}</th>
                      <th style="width: 20%">{{ $t('work_methods.attribute_key') }}</th>
                      <th style="width: 12%">{{ $t('work_methods.value_type') }}</th>
                      <th style="width: 8%">{{ $t('work_methods.unit') }}</th>
                      <th style="width: 14%">{{ $t('work_methods.show_in_report') }}</th>
                      <th v-if="group.key === 'DAY'" style="width: 14%">{{ $t('work_methods.cumulative') }}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <draggable v-model="method[group.field]" tag="tbody" handle=".drag-handle" @end="onReorder(method)">
                    <tr v-for="def in method[group.field]" :key="def.id">
                      <td class="drag-handle" style="cursor: move">
                        <v-icon small>mdi-drag</v-icon>
                      </td>
                      <td>{{ def.label }}</td>
                      <td class="text--secondary">{{ def.attributeKey }}</td>
                      <td>{{ $t('work_methods.value_types.' + def.valueType) }}</td>
                      <td>{{ def.unit }}</td>
                      <td><v-icon v-if="def.showInReport" small>mdi-check</v-icon></td>
                      <td v-if="group.key === 'DAY'"><v-icon v-if="def.cumulative" small>mdi-sigma</v-icon></td>
                      <td class="text-right" style="white-space: nowrap">
                        <v-btn icon small @click="editDef(method, def)">
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon small @click="openDefRemoveDialog(def)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </draggable>
                </template>
              </v-simple-table>
            </div>
            <v-btn text color="primary" class="mt-2" @click="addDef(method)">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('work_methods.add_attribute') }}
            </v-btn>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <confirm-dialog ref="defRemoveDialog" />
      <work-method-dialog
        ref="workMethodDialog"
        @created="notifyAndReload($t('work_methods.method_created'))"
        @updated="notifyAndReload($t('work_methods.method_updated'))"
      />
      <attribute-def-dialog
        ref="attributeDefDialog"
        @created="notifyAndReload($t('work_methods.attribute_created'))"
        @updated="notifyAndReload($t('work_methods.attribute_updated'))"
      />
    </v-col>
  </hb-basic-page>
</template>

<script>
import draggable from 'vuedraggable';
import workMethodsApi from '@/api/workMethods';
import ConfirmDialog from '@/components/ConfirmDialog';
import WorkMethodDialog from '@/components/WorkMethods/WorkMethodDialog';
import AttributeDefDialog from '@/components/WorkMethods/AttributeDefDialog';

export default {
  name: 'WorkMethodsView',
  components: { AttributeDefDialog, WorkMethodDialog, ConfirmDialog, draggable },
  data() {
    return {
      loading: true,
      methods: [],
      // Attribuutit näytetään ryhmiteltyinä kohteen (scope) mukaan: työn tiedot ensin, sitten päiväkohtaiset.
      attributeGroups: [
        { key: 'WORK', field: 'workAttrs', titleKey: 'work_methods.work_info_section' },
        {
          key: 'DAY',
          field: 'dayAttrs',
          titleKey: 'work_methods.daily_info_section',
          hintKey: 'work_methods.daily_info_hint',
        },
      ],
    };
  },
  methods: {
    pricingLabel(method) {
      return method.attributes.find(a => a.id === method.pricingDefId)?.label || this.$t('work_methods.pricing_lot');
    },
    addMethod() {
      this.$refs.workMethodDialog.open(null);
    },
    editMethod(method) {
      this.$refs.workMethodDialog.open(method);
    },
    addDef(method) {
      this.$refs.attributeDefDialog.open(method.id, null);
    },
    editDef(method, def) {
      this.$refs.attributeDefDialog.open(method.id, def);
    },
    openDefRemoveDialog(def) {
      this.$refs.defRemoveDialog
        .open(
          this.$t('work_methods.delete_attribute_dialog.title'),
          this.$t('work_methods.delete_attribute_dialog.description'),
          { color: 'error' }
        )
        .then(confirm => {
          if (confirm === true) {
            this.removeDef(def);
          }
        });
    },
    async removeDef(def) {
      try {
        await workMethodsApi.deleteAttributeDef(def.id);
        this.$showSuccessNotification(this.$t('work_methods.attribute_deleted'));
        await this.reloadMethods();
      } catch (err) {
        if (err.response && err.response.status === 409) {
          this.$showErrorNotification(this.$t('work_methods.errors.has_values'));
        } else {
          this.$handleApiError(err);
        }
      }
    },
    async notifyAndReload(message) {
      this.$showSuccessNotification(message);
      await this.reloadMethods();
    },
    async reloadMethods() {
      try {
        const methods = await workMethodsApi.getAllWorkMethodsAdmin();
        // Jaetaan attribuutit ryhmiin (scope) raahausta varten; lähde on jo displayOrder-järjestyksessä.
        this.methods = methods.map(m => ({
          ...m,
          workAttrs: m.attributes.filter(a => a.scope === 'WORK'),
          dayAttrs: m.attributes.filter(a => a.scope === 'DAY'),
        }));
      } catch (err) {
        console.log(err);
        this.$showErrorNotification(this.$t('work_methods.errors.load_failed'));
      }
    },
    async onReorder(method) {
      // Lähetetään koko menetelmän järjestys WORK-ryhmä ensin, DAY-ryhmä perässä.
      const orderedIds = [...method.workAttrs, ...method.dayAttrs].map(d => d.id);
      try {
        await workMethodsApi.reorderAttributeDefs(method.id, orderedIds);
      } catch (err) {
        this.$handleApiError(err);
        await this.reloadMethods();
      }
    },
  },
  async mounted() {
    this.loading = true;
    await this.reloadMethods();
    this.loading = false;
  },
};
</script>

<style scoped>
/* Kiinteä sarakeleveys, jotta työn tietojen ja päiväkohtaisten tietojen taulukoiden
   yhteiset sarakkeet asettuvat samoihin kohtiin. */
.aligned-attr-table ::v-deep table {
  table-layout: fixed;
}
</style>
